import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useDeals, useStakeDebit, useUnclaimedTransactions, useUserAccount } from "../../CustomHooks/Queries";
import { useUpdateAddress } from "../../CustomHooks/Mutations";
import {
  BATTLECARD_FLOW_NAV_STATE,
  BATTLECARD_PLAN_SELECTION_NAV_STATE,
  dispatchErrorToast,
  getCheckIdFromDealsResponse,
  getUnclaimedCashBackAmount,
  shouldHideLegacyPlanUpgradeOptions,
} from "../../Utils/helpers";
import { APIQueryKeys } from "../../Utils/constants";
import {
  addressFromUser,
  buildUpdateAddressPayload,
  isAddressComplete,
  trimAddressFields,
} from "./check-address-utils";
import ValidateAddressModal from "./validate-address-modal";
import LegacyCheckPageLayout from "./legacy-check-page-layout";
import "./battle-cards.scss";

export function WarningIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden>
      <circle cx="24" cy="24" r="20" fill="none" stroke="#D4714A" strokeWidth="2.5" />
      <rect x="22" y="13" width="4" height="14" rx="2" fill="#D4714A" />
      <rect x="22" y="31" width="4" height="4" rx="2" fill="#D4714A" />
    </svg>
  );
}

function HelpIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
      <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M7.75 7.5a2.25 2.25 0 0 1 4.25 1c0 1.5-2.25 1.75-2.25 3.25"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="9.75" cy="14.75" r="0.9" fill="currentColor" />
    </svg>
  );
}

export default function CheckWarning() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showValidateAddress, setShowValidateAddress] = useState(false);
  const [showVerifyError, setShowVerifyError] = useState(false);
  const { data: dealsData } = useDeals();
  const { data: stakeDebitData } = useStakeDebit();
  const { data: unclaimedTransactionsData } = useUnclaimedTransactions();
  const { data: userAccountData } = useUserAccount();

  const user = userAccountData?.data?.data?.user;
  const [draft, setDraft] = useState(() => addressFromUser(user));

  const hidePlanUpgradeOptions = shouldHideLegacyPlanUpgradeOptions(dealsData, stakeDebitData);

  const cashBackAmount = useMemo(
    () => getUnclaimedCashBackAmount(unclaimedTransactionsData),
    [unclaimedTransactionsData]
  );

  const { mutate: updateAddress, isLoading: isVerifyingAddress } = useUpdateAddress({
    onSuccess: async () => {
      await queryClient.refetchQueries(APIQueryKeys.userAccount);
      setShowValidateAddress(false);
      setShowVerifyError(false);
      navigate("/battlecard/check-mailing-address", { state: BATTLECARD_FLOW_NAV_STATE });
    },
    onError: () => {
      setShowVerifyError(true);
    },
  });

  function openValidateAddress() {
    setDraft(addressFromUser(user));
    setShowVerifyError(false);
    setShowValidateAddress(true);
  }

  function updateDraftField(field, value) {
    setShowVerifyError(false);
    setDraft((prev) => ({ ...prev, [field]: value }));
  }

  function handleVerifyAddress() {
    const trimmed = trimAddressFields(draft);

    if (!isAddressComplete(trimmed)) {
      dispatchErrorToast("Please fill in all required address fields.");
      return;
    }

    setShowVerifyError(false);

    updateAddress(
      buildUpdateAddressPayload(trimmed, {
        withdrawalId: getCheckIdFromDealsResponse(dealsData),
      })
    );
  }

  return (
    <LegacyCheckPageLayout>
      <div className="battle-cards-page bc-check-warning-page">
        <div className="bc-check-warning-card">
          <button
            type="button"
            className="bc-check-warning-help"
            aria-label="About check arrival times"
            aria-expanded={showDisclaimer}
            onClick={() => setShowDisclaimer((prev) => !prev)}
          >
            <HelpIcon />
          </button>
          {showDisclaimer && (
            <div className="bc-check-warning-disclaimer" role="tooltip">
              Mailed checks are processed and delivered by a third-party service. Arrival
              typically takes 4-5 months and timing isn't guaranteed. Opening a Stake Account
              gets your Cash Back to you today.
            </div>
          )}
          <WarningIcon />
          <h2 className="bc-check-warning-title">Get your Cash Back today instead</h2>
          <p className="bc-check-warning-body">
            {`Checks take 4-5 months to arrive. Open a Stake Account and your ${cashBackAmount} arrives today.`}
          </p>
          <div className="bc-check-warning-actions">
            <button
              type="button"
              className="bc-check-warning-btn secondary"
              onClick={openValidateAddress}
            >
              Confirm my address
            </button>
            {!hidePlanUpgradeOptions && (
              <button
                type="button"
                className="bc-check-warning-btn primary"
                onClick={() => navigate("/battlecard", { state: BATTLECARD_PLAN_SELECTION_NAV_STATE })}
              >
                Change my plan
              </button>
            )}
          </div>
        </div>
        {showValidateAddress && (
          <ValidateAddressModal
            draft={draft}
            onFieldChange={updateDraftField}
            onVerify={handleVerifyAddress}
            onCancel={() => setShowValidateAddress(false)}
            isVerifying={isVerifyingAddress}
            showVerifyError={showVerifyError}
          />
        )}
      </div>
    </LegacyCheckPageLayout>
  );
}
